export const SELECTED_PARTNER_STORAGE_KEY = 'partner';
export const SELECTED_PARTNER_ID_STORAGE_KEY = 'selectedPartnerId';

export const PARTNER_ID_QUERY_KEYS = ['partnerId', 'partner_id'];
export const PARTNER_CODE_QUERY_KEYS = ['code', 'partnerCode', 'tlf_utm'];
export const PARTNER_GENERIC_QUERY_KEYS = ['partner'];
export const PARTNER_QUERY_KEYS = [...PARTNER_ID_QUERY_KEYS, ...PARTNER_GENERIC_QUERY_KEYS];

const firstValue = (...values) => {
  for (const value of values) {
    if (value != null && value !== '') return String(value);
  }
  return '';
};

const normalizeMatchValue = (value) => String(value || '').trim().toLowerCase();

// Tracks partner IDs/codes from the URL that were checked against the
// organization's partner list and found to be unknown, preventing location
// re-reads from resurrecting a rejected URL parameter.
const invalidatedPartnerValues = new Set();

const isInvalidatedValue = (value) => {
  const norm = normalizeMatchValue(value);
  return norm ? invalidatedPartnerValues.has(norm) : false;
};

const getSessionStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

const getRuntimePartner = () => {
  if (typeof window === 'undefined') return null;
  return window.SITE_CONFIG?.runtime?.partner || window.Partner || null;
};

export const normalizePartnerSelection = (partner) => {
  if (!partner) return null;

  const id = firstValue(
    partner.id,
    partner.Id,
    partner.partnerId,
    partner.PartnerId,
    partner.ticketTrackingPartnerId,
    partner.ticketTrackingPartnerID
  );
  const code = firstValue(partner.Code, partner.code, partner.partnerCode, partner.PartnerCode);
  const name = firstValue(
    partner.organization_name,
    partner.organizationName,
    partner.partner_name,
    partner.partnerName,
    partner.PartnerName,
    partner.name
  );
  const logoUrl = firstValue(
    partner.organization_logo_url,
    partner.organizationLogoUrl,
    partner.partner_logo_url,
    partner.partnerLogoUrl,
    partner.PartnerLogo,
    partner.logo_url,
    partner.logoUrl
  );

  if (!id && !code) return null;

  return {
    ...partner,
    id,
    code,
    name,
    logoUrl,
  };
};

export const storeSelectedPartner = (partner) => {
  const storage = getSessionStorage();
  if (!storage) return null;

  const normalizedPartner = normalizePartnerSelection(partner);
  if (!normalizedPartner) {
    storage.removeItem(SELECTED_PARTNER_STORAGE_KEY);
    storage.removeItem(SELECTED_PARTNER_ID_STORAGE_KEY);
    return null;
  }

  // If a partner is already stored with the same id or code, preserve its
  // richer attributes (name, code, logoUrl, custom properties) when the incoming
  // selection is a stub (e.g. captured from a URL parameter like ?partnerId=...).
  const existing = getStoredSelectedPartner();
  if (existing) {
    const sameId = normalizedPartner.id && existing.id && normalizeMatchValue(normalizedPartner.id) === normalizeMatchValue(existing.id);
    const sameCode = normalizedPartner.code && existing.code && normalizeMatchValue(normalizedPartner.code) === normalizeMatchValue(existing.code);
    if (sameId || sameCode) {
      if (!normalizedPartner.id && existing.id) normalizedPartner.id = existing.id;
      if (!normalizedPartner.code && existing.code) normalizedPartner.code = existing.code;
      if (!normalizedPartner.name && existing.name) normalizedPartner.name = existing.name;
      if (!normalizedPartner.logoUrl && existing.logoUrl) normalizedPartner.logoUrl = existing.logoUrl;

      Object.keys(existing).forEach((key) => {
        if (normalizedPartner[key] == null || normalizedPartner[key] === '') {
          normalizedPartner[key] = existing[key];
        }
      });
    }
  }

  // Enrich from runtime partner if id or code matches
  const runtime = normalizePartnerSelection(getRuntimePartner());
  if (runtime) {
    const sameRuntimeId = normalizedPartner.id && runtime.id && normalizeMatchValue(normalizedPartner.id) === normalizeMatchValue(runtime.id);
    const sameRuntimeCode = normalizedPartner.code && runtime.code && normalizeMatchValue(normalizedPartner.code) === normalizeMatchValue(runtime.code);
    if (sameRuntimeId || sameRuntimeCode) {
      if (!normalizedPartner.id && runtime.id) normalizedPartner.id = runtime.id;
      if (!normalizedPartner.code && runtime.code) normalizedPartner.code = runtime.code;
      if (!normalizedPartner.name && runtime.name) normalizedPartner.name = runtime.name;
      if (!normalizedPartner.logoUrl && runtime.logoUrl) normalizedPartner.logoUrl = runtime.logoUrl;
    }
  }

  storage.setItem(SELECTED_PARTNER_STORAGE_KEY, JSON.stringify(normalizedPartner));
  if (normalizedPartner.id) {
    storage.setItem(SELECTED_PARTNER_ID_STORAGE_KEY, normalizedPartner.id);
    invalidatedPartnerValues.delete(normalizeMatchValue(normalizedPartner.id));
  }
  if (normalizedPartner.code) {
    invalidatedPartnerValues.delete(normalizeMatchValue(normalizedPartner.code));
  }

  return normalizedPartner;
};

export const invalidateSelectedPartner = (partner) => {
  if (partner) {
    const idNorm = normalizeMatchValue(typeof partner === 'string' ? partner : partner.id);
    const codeNorm = normalizeMatchValue(typeof partner === 'string' ? partner : partner.code);
    if (idNorm) invalidatedPartnerValues.add(idNorm);
    if (codeNorm) invalidatedPartnerValues.add(codeNorm);
  }
  storeSelectedPartner(null);
  return null;
};

export const clearInvalidatedPartners = () => {
  invalidatedPartnerValues.clear();
};

export const getStoredSelectedPartner = () => {
  const storage = getSessionStorage();
  if (!storage) return null;

  try {
    const storedPartner = storage.getItem(SELECTED_PARTNER_STORAGE_KEY);
    if (storedPartner) return normalizePartnerSelection(JSON.parse(storedPartner));
  } catch {
    storage.removeItem(SELECTED_PARTNER_STORAGE_KEY);
  }

  const storedPartnerId = storage.getItem(SELECTED_PARTNER_ID_STORAGE_KEY);
  return storedPartnerId ? normalizePartnerSelection({ id: storedPartnerId }) : null;
};

export const getSelectedPartner = () => {
  const storedPartner = getStoredSelectedPartner();
  if (storedPartner) return storedPartner;

  // The runtime partner is only populated (server-side bootstrap or window.Partner)
  // when the request URL explicitly carried a partner reference (?tlf_utm, ?partner, /partner, etc.). That is an
  // explicit, intentional selection, so honor it for attribution even when the
  // partner is hidden from the public dropdown (isVisible === false). The
  // isVisible flag only governs whether a partner appears in the selectable list.
  const runtimePartner = normalizePartnerSelection(getRuntimePartner());
  if (runtimePartner) return runtimePartner;

  return null;
};

export const hasSelectedPartnerIdentity = (partner) => !!(partner?.id || partner?.code);

export const hasVerifiedPartnerSelection = (partner, partners = null) => {
  const normalized = normalizePartnerSelection(partner);
  if (!normalized) return false;
  if (Array.isArray(partners) && partners.length > 0) {
    return !!resolvePartnerFromList(partners, normalized);
  }
  return !!(normalized.id && normalized.name);
};

export const readPartnerSelectionFromLocation = (location = typeof window !== 'undefined' ? window.location : null) => {
  if (!location) return null;

  const searchParams = new URLSearchParams(location.search || '');
  let partnerId = '';
  let partnerCode = '';

  for (const key of PARTNER_ID_QUERY_KEYS) {
    partnerId = firstValue(partnerId, searchParams.get(key));
  }
  for (const key of PARTNER_CODE_QUERY_KEYS) {
    partnerCode = firstValue(partnerCode, searchParams.get(key));
  }
  for (const key of PARTNER_GENERIC_QUERY_KEYS) {
    const genericVal = searchParams.get(key);
    if (genericVal) {
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(genericVal)) {
        partnerId = firstValue(partnerId, genericVal);
      } else {
        partnerCode = firstValue(partnerCode, genericVal);
      }
    }
  }

  const codePathMatch = String(location.pathname || '').match(/\/code\/([^/?#]+)/i);
  if (!partnerCode && codePathMatch?.[1]) {
    partnerCode = decodeURIComponent(codePathMatch[1]);
  }

  // Also support the /partner/:idOrCode landing path. The path segment may be a
  // partner GUID (id) or a human-friendly slug/code, so classify it accordingly
  // and let downstream matching resolve by either id or code.
  const partnerPathMatch = String(location.pathname || '').match(/\/partner\/([^/?#]+)/i);
  if (partnerPathMatch?.[1]) {
    const pathValue = decodeURIComponent(partnerPathMatch[1]);
    const looksLikeGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(pathValue);
    if (looksLikeGuid) {
      partnerId = firstValue(partnerId, pathValue);
    } else if (!partnerCode) {
      partnerCode = pathValue;
    }
  }

  const runtime = normalizePartnerSelection(getRuntimePartner());
  if (runtime) {
    if (partnerCode && normalizeMatchValue(runtime.code) === normalizeMatchValue(partnerCode)) {
      partnerId = partnerId || runtime.id;
    } else if (partnerId && normalizeMatchValue(runtime.id) === normalizeMatchValue(partnerId)) {
      partnerCode = partnerCode || runtime.code;
    }
  }

  const normalized = normalizePartnerSelection({ id: partnerId, code: partnerCode });
  if (!normalized) return null;
  if (
    (normalized.id && isInvalidatedValue(normalized.id)) ||
    (normalized.code && isInvalidatedValue(normalized.code))
  ) {
    return null;
  }

  const existing = getStoredSelectedPartner();
  if (existing && normalized) {
    const sameId = normalized.id && existing.id && normalizeMatchValue(normalized.id) === normalizeMatchValue(existing.id);
    const sameCode = normalized.code && existing.code && normalizeMatchValue(normalized.code) === normalizeMatchValue(existing.code);
    if (sameId || sameCode) {
      return {
        ...existing,
        ...normalized,
        id: normalized.id || existing.id,
        code: normalized.code || existing.code,
        name: normalized.name || existing.name,
        logoUrl: normalized.logoUrl || existing.logoUrl,
      };
    }
  }

  if (runtime && (
    (normalized.id && normalizeMatchValue(normalized.id) === normalizeMatchValue(runtime.id)) ||
    (normalized.code && normalizeMatchValue(normalized.code) === normalizeMatchValue(runtime.code))
  )) {
    return {
      ...runtime,
      ...normalized,
      id: normalized.id || runtime.id,
      code: normalized.code || runtime.code,
      name: normalized.name || runtime.name,
      logoUrl: normalized.logoUrl || runtime.logoUrl,
    };
  }

  return normalized;
};

export const capturePartnerSelectionFromLocation = (location) => {
  const partner = readPartnerSelectionFromLocation(location);
  if (partner) storeSelectedPartner(partner);
  return partner;
};

export const appendSelectedPartnerToPath = (path) => {
  const selectedPartner = getSelectedPartner() || capturePartnerSelectionFromLocation();
  if (!selectedPartner || (!selectedPartner.id && !selectedPartner.code)) return path;

  try {
    const url = new URL(path, window.location.origin);
    const hasPartnerParam = PARTNER_QUERY_KEYS.concat(PARTNER_CODE_QUERY_KEYS)
      .some((key) => url.searchParams.has(key));
    if (!hasPartnerParam) {
      if (selectedPartner.id) {
        url.searchParams.set('partnerId', selectedPartner.id);
      } else if (selectedPartner.code) {
        url.searchParams.set('partnerCode', selectedPartner.code);
      }
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return path;
  }
};

export const findPartnerByIdOrCode = (partners, value) => {
  const normalizedValue = normalizeMatchValue(value);
  if (!normalizedValue) return null;

  return (partners || []).map(normalizePartnerSelection).find((partner) => (
    (partner?.id && normalizeMatchValue(partner.id) === normalizedValue) ||
    (partner?.code && normalizeMatchValue(partner.code) === normalizedValue)
  )) || null;
};

export const resolvePartnerFromList = (partners, selectedPartner = getSelectedPartner()) => {
  const normalizedSelectedPartner = normalizePartnerSelection(selectedPartner);
  if (!normalizedSelectedPartner) return null;
  if (!Array.isArray(partners) || partners.length === 0) return null;
  return (
    (normalizedSelectedPartner.id && findPartnerByIdOrCode(partners, normalizedSelectedPartner.id)) ||
    (normalizedSelectedPartner.code && findPartnerByIdOrCode(partners, normalizedSelectedPartner.code)) ||
    null
  );
};

const getPartnerFieldMatch = (field, selectedPartner) => {
  const normalizedPartner = normalizePartnerSelection(selectedPartner);
  if (!normalizedPartner) return null;

  let partnerId = normalizeMatchValue(normalizedPartner.id);
  const partnerCode = normalizeMatchValue(normalizedPartner.code);

  if (!partnerId && partnerCode) {
    const runtime = normalizePartnerSelection(getRuntimePartner());
    if (runtime && normalizeMatchValue(runtime.code) === partnerCode && runtime.id) {
      partnerId = normalizeMatchValue(runtime.id);
    }
  }

  const options = field?.ticketTrackingFieldOptions || field?.options || [];
  return options.find((option) => (
    (partnerId && (
      normalizeMatchValue(option.ticketTrackingPartnerId) === partnerId ||
      normalizeMatchValue(option.ticketTrackingPartnerID) === partnerId ||
      normalizeMatchValue(option.TicketTrackingPartnerId) === partnerId ||
      normalizeMatchValue(option.TicketTrackingPartnerID) === partnerId ||
      normalizeMatchValue(option.partnerId) === partnerId
    )) ||
    (partnerCode && (
      normalizeMatchValue(option.partnerCode) === partnerCode ||
      normalizeMatchValue(option.PartnerCode) === partnerCode ||
      normalizeMatchValue(option.Code) === partnerCode ||
      normalizeMatchValue(option.code) === partnerCode
    )) ||
    (!partnerId && normalizedPartner.name && (
      normalizeMatchValue(option.fieldValue) === normalizeMatchValue(normalizedPartner.name) ||
      normalizeMatchValue(option.FieldValue) === normalizeMatchValue(normalizedPartner.name)
    ))
  )) || null;
};

export const isPartnerTrackingField = (field) => {
  const options = field?.ticketTrackingFieldOptions || field?.options || [];
  return options.some((option) => (
    option.ticketTrackingPartnerId != null ||
    option.ticketTrackingPartnerID != null ||
    option.TicketTrackingPartnerId != null ||
    option.TicketTrackingPartnerID != null ||
    option.partnerId != null ||
    option.partnerCode != null ||
    option.PartnerCode != null ||
    option.Code != null ||
    option.code != null
  ));
};

export const applyPartnerToTrackingFields = (fields, selectedPartner = getSelectedPartner()) => (
  (fields || []).map((field) => {
    const match = getPartnerFieldMatch(field, selectedPartner);
    if (!match) {
      if (selectedPartner !== undefined && isPartnerTrackingField(field)) {
        const nextField = { ...field };
        delete nextField.Selection;
        return nextField;
      }
      return { ...field };
    }
    return { ...field, Selection: match.id };
  })
);

export const updateTrackingSelectionsForPartner = (fields, selections, selectedPartner) => {
  const nextSelections = { ...(selections || {}) };

  (fields || []).forEach((field) => {
    if (!isPartnerTrackingField(field)) return;

    const match = getPartnerFieldMatch(field, selectedPartner);
    if (match?.id != null) {
      nextSelections[field.id] = match.id;
    } else {
      delete nextSelections[field.id];
    }
  });

  return nextSelections;
};

export const buildTicketTrackingEntries = (selections, cartItemIdReference) => (
  Object.entries(selections || {})
    .filter(([, optionId]) => optionId && optionId !== 'none')
    .map(([fieldId, optionId]) => ({
      CartItemIdReference: cartItemIdReference || undefined,
      FieldId: fieldId,
      OptionId: optionId,
    }))
);